//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  useEffect, useLayoutEffect, useRef, useState
} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Mark from 'mark.js';
import {
  Box, Button, Chip, Divider, IconButton, InputAdornment, TextField,
  Tooltip, Typography
} from '@mui/material';
import { lighten, styled, Theme, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import CircularProgress from '@mui/material/CircularProgress';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StopIcon from '@mui/icons-material/Stop';
import { type ApprovalDecision, type TraceEntry } from '@/agent/contract';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { useEnterSubmitHandler } from '@/hooks/useEnterSubmitHandler';
import ApprovalCard from './ApprovalCard';
import { CHAT_TOUR_ANCHORS } from './chatPanelId';
import { datasetNotes, datasetNoteText } from './datasetNotes';
import { ChatMessage } from './ChatTypes';
import TraceList from './TraceList';
import UnappliedAction from './UnappliedAction';

// Names the hidden hint that tells assistive technology how to accept the
// suggested prompt.
const NO_MODEL_HINT_ID = 'chat-no-model-hint';
// The one wording of the no-model state; the placeholder shows the first
// sentence and the helper text the whole.
const NO_MODEL_SHORT = 'No AI model is available';
const NO_MODEL_TEXT = `${NO_MODEL_SHORT}. Ask an administrator to enable one.`;
// How far from the newest end a reader may be and still count as there,
// so a sub-pixel or rounding gap does not stop the transcript following.
const FOLLOW_SLACK_PX = 4;

// How many datasets besides the header's get a row of suggested prompts.
// Enough to show the assistant is not bound to the header's choice without
// pushing the message box out of a short panel.
const OTHER_DATASET_ROWS = 4;

// The two starting points offered for a dataset.
const promptsFor = (dataset: string): string[] => [
  `Tell me about the ${dataset} graph.`,
  `What can I learn from the ${dataset} graph?`
];

// Sending while a turn is already running, or while one is paused on an
// approval nobody has decided yet, would only draw the backend's 409
// refusal for either case; sending before the dataset list has answered
// would tell the model there is no dataset when the header does not know
// yet. Keyed by the reason so the message box can name the one that
// applies, the same way the no-model state names its own.
type SendBlockReason = 'turn' | 'approval' | 'datasets';
const SEND_BLOCK_TEXT: Record<SendBlockReason, {
  short: string; text: string; hintId: string; ariaLabel: string;
}> = {
  turn: {
    short: 'A turn is already running',
    text: 'A turn is already running. Sending will resume once it ' +
          'finishes.',
    hintId: 'chat-turn-hint',
    ariaLabel: 'Message (a turn is already running)'
  },
  approval: {
    short: 'An approval is waiting for your decision',
    text: 'An approval is waiting for your decision. Decide above ' +
          'before sending another message.',
    hintId: 'chat-approval-hint',
    ariaLabel: 'Message (an approval is waiting for your decision)'
  },
  datasets: {
    short: 'The dataset list is still loading',
    text: 'The dataset list is still loading. Sending will resume once ' +
          'the header knows your dataset.',
    hintId: 'chat-datasets-hint',
    ariaLabel: 'Message (the dataset list is still loading)'
  }
};

// The step of a running turn worth announcing: the latest tool step. A live
// region interrupts a screen reader on every change, so a region following
// the trace entry for entry would talk over the reader for the whole turn --
// and the thought and result around a tool step are that same step in other
// words. The steps themselves are on screen in TraceList either way.
const latestToolStep = (entries: readonly TraceEntry[]): TraceEntry | null => {
  for (let index = entries.length - 1; index >= 0; index--)
    if (entries[index].kind === 'tool') return entries[index];
  return null;
};

// How a finished turn reads: what became of it, not the reply itself. The
// transcript is there to be read at the reader's own pace, and a reply read
// aloud in full the moment it lands is not an announcement.
const turnOutcome = (messages: readonly ChatMessage[]): string => {
  const last = messages.at(-1);
  if (last === undefined || last.role !== 'bot') return 'The turn finished.';
  if (last.stopped) return 'The turn was stopped.';
  if (last.isError) return 'The turn ended with an error.';
  if (last.approval?.decision === null)
    return 'The assistant is asking for your approval.';
  return 'The assistant replied.';
};

interface MessageProps {
  msgRole: 'user' | 'bot';
}

interface ChatMessageItemProps {
  msg: ChatMessage;
  theme: Theme;
}

// A line of the transcript that is neither side's: it says the dataset
// the turns after it work on changed.  Kept out of .chat-message so the
// transcript search matches what the user and the model said.
const DatasetNote = styled('div')(({ theme }) => ({
  margin: '4px 17px 10px',
  textAlign: 'center',
  fontSize: '0.8rem',
  color: theme.palette.text.secondary
}));

const Message = styled('div', {
  shouldForwardProp: (prop) => prop !== 'msgRole',
})<MessageProps>(({ theme, msgRole }) => ({
  marginBottom: '10px',
  padding: '1px 17px',
  textAlign: 'left',
  ...(msgRole === 'user' && {
    backgroundColor: theme.palette.chatbot.user,
    marginLeft: '20%',
    borderRadius: '8px',
  }),
  ...(msgRole === 'bot' && {
    textAlign: 'left',
  }),
}));

// Memoized message component to prevent React from reconciling the DOM and
// removing manual highlights applied by mark.js.
const ChatMessageItem = React.memo(({ msg, theme }: ChatMessageItemProps) => {
  return (
    <Message msgRole={msg.role} className="chat-message">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Block code in react-markdown v9 renders as <pre><code>.
          // Intercept at the pre level — pre only ever appears for block code
          // in markdown, so this reliably catches both labeled (```lang) and
          // unlabeled (```) fences without needing the removed `inline` prop.
          pre({ children }) {
            const codeEl = React.Children.toArray(children)[0];
            if (React.isValidElement<{ children?: unknown }>(codeEl)) {
              const code = String(codeEl.props.children ?? '');
              return <CodeBlock>{code.replace(/\n$/, '')}</CodeBlock>;
            }
            return <pre>{children}</pre>;
          },
          code({ className, children, ...props }) {
            // Only inline code reaches here; block code is handled via pre.
            return <code className={className} {...props}>{children}</code>;
          },
          blockquote: ({ children }) => {
            if (msg.isError) {
              return (
                <div
                  style={{
                    borderLeft: `4px solid ${theme.palette.error.main}`,
                    paddingLeft: '1rem',
                    backgroundColor: lighten(theme.palette.error.main, 0.9),
                    margin: '8px 0',
                    borderRadius: '4px',
                  }}
                >
                  {children}
                </div>
              );
            }
            return <blockquote style={{ borderLeft: '4px solid #ccc', paddingLeft: '1rem' }}>{children}</blockquote>;
          }
        }}
      >
        {msg.content}
      </Markdown>
    </Message>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if content or role changes.
  // This is crucial for mark.js persistence.
  return prevProps.msg.content === nextProps.msg.content &&
         prevProps.msg.role === nextProps.msg.role &&
         prevProps.msg.isError === nextProps.msg.isError;
});

ChatMessageItem.displayName = 'ChatMessageItem';

// A reply's text shown as a bot message of its own: the streaming reply, or
// what a stopped turn had streamed. Memoized on the text: a message object
// built at the call site would be new on every render, typing included, and
// defeat ChatMessageItem's own memo.
interface PartialReplyProps {
  content: string;
  theme: Theme;
}

const PartialReply = React.memo(({ content, theme }: PartialReplyProps) => (
  <ChatMessageItem msg={{ role: 'bot', content }} theme={theme} />
));

PartialReply.displayName = 'PartialReply';

export interface ChatContentProps {
  activeLlmName: string;
  // No model to send to: an admin has disabled every one, or none is
  // configured. Decided by the owner of the config, not inferred here from
  // an empty name, which a config that has not loaded yet also produces.
  noModel: boolean;
  chatMessages: ChatMessage[];
  // Whether what this renders is on screen. Its only consumer is the chat
  // panel, which hides rather than unmounts, so this is required.
  isVisible: boolean;
  // Whether this showing may move focus into the message box. False when
  // the session restored the panel rather than the user opening it -- the
  // same signal the panel host consults before focusing its own frame.
  takesFocus: boolean;
  onClearChatHistory: () => void;
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  // The running turn's steps, empty when idle.
  trace: TraceEntry[];
  // The reply text the running turn has streamed so far, empty when none;
  // shown as the pending assistant message until the reply replaces it.
  partialText: string;
  // A message in the transcript carries an approval nobody has decided
  // yet. Computed by ChatPanelBody, which owns the transcript shape.
  hasPendingApproval: boolean;
  // Bumped by ChatPanelBody each time a turn this session ran ends in an
  // approval. Focus moves to the card on that, rather than on the card
  // appearing: a reopened conversation renders an outstanding approval
  // exactly the same way, and taking focus for it on page load would drag
  // the user out of whatever they were doing.
  approvalArrivalCount: number;
  // Bumped by ChatPanelBody each time a decision is actually written to the
  // transcript, so focus can return to the message box without this having
  // to guess why hasPendingApproval changed -- that flag also drops when
  // the transcript is cleared or a resumed conversation loads with nothing
  // pending, neither of which should move focus.
  approvalDecisionCount: number;
  // A decision has been sent and its turn is running.
  isDeciding: boolean;
  onDecide: (approvalId: string, decision: ApprovalDecision) => void;
  // The stop has been sent and the poll is waiting for the backend to
  // confirm it.
  isStopping: boolean;
  // Bumped each time a stop request fails with the turn still running, so
  // the failure is announced from the failure itself rather than inferred
  // from isStopping falling while isLoading has yet to.
  stopFailureCount: number;
  // There is a turn in flight to stop. False while createConversation or
  // sendTurn is still posting and the async id Stop would cancel does not
  // exist yet.
  canStop: boolean;
  onStop: () => void;
  onNavigate: (path: string) => void;
  // The header's dataset, which the next turn carries; '' when there is
  // none.
  // The header's dataset, which the next turn carries; null for none.
  activeDataset: string | null;
  // The other datasets the header could select, in the header's order.
  // Each gets a row of suggested prompts below the active dataset's, up to
  // OTHER_DATASET_ROWS of them.
  otherDatasets: readonly string[];
  // The dataset list's initial load has not answered, so the header's
  // dataset is unknown rather than none.
  datasetsLoading: boolean;
}

const ChatContent = ({
  activeLlmName, noModel, chatMessages, isVisible, takesFocus,
  onClearChatHistory, isLoading, onSendMessage, trace, partialText,
  hasPendingApproval,
  approvalArrivalCount, approvalDecisionCount, isDeciding, onDecide,
  isStopping, stopFailureCount, canStop, onStop, onNavigate,
  activeDataset, otherDatasets, datasetsLoading
}: ChatContentProps) => {
  const theme = useTheme();
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [question, setQuestion] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  // Track previous search term to detect when a *new* search starts vs just
  // navigating.
  const prevSearchTermRef = useRef<string>('');
  // Prevent scrolling during refinement (typing) to avoid jumps.
  const scrollToMatchRef = useRef<number | null>(null);
  // Visibility as of the last effect run. False to start with, so mounting
  // already on screen counts as becoming visible: the panel registers itself
  // at the moment it opens, so that is the ordinary way chat appears.
  const wasVisible = useRef(false);

  const MATCH_COLOR = theme.colors.solarFlare.p20;
  const ACTIVE_COLOR = theme.colors.solarFlare.p50;

  // Scroll to the newest message, unless a search is running -- navigating
  // matches moves the transcript itself, and this would fight it.
  //
  // A hidden panel is display:none, where scrollHeight is 0 and this write
  // moves nothing, so the write has to wait for the panel to come back. There
  // is no reading position to preserve across that: display:none drops the
  // scroll offset of everything beneath it, so a reveal starts at the top of
  // the transcript whatever the reader was looking at. Newest is the useful
  // end to land on. Before paint, so the reveal never shows a frame at the
  // top first.
  useLayoutEffect(() => {
    if (!isVisible || !chatHistoryRef.current || searchTerm) return;
    chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
  }, [chatMessages, isVisible, searchTerm]);

  // Whether the reader is at the newest end of the transcript, as of the
  // last scroll. Read when the streaming reply grows: a reader there is
  // kept there, while one who scrolled up to reread is left alone rather
  // than dragged down on every poll. True to start with, since a transcript
  // that has never scrolled is at its end.
  const followsNewest = useRef(true);
  const handleTranscriptScroll = () => {
    const node = chatHistoryRef.current;
    if (!node) return;
    followsNewest.current =
      node.scrollHeight - node.scrollTop - node.clientHeight <=
      FOLLOW_SLACK_PX;
  };
  useLayoutEffect(() => {
    if (!isVisible || !chatHistoryRef.current || searchTerm || !partialText)
      return;
    if (followsNewest.current)
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
  }, [partialText, isVisible, searchTerm]);

  // Focus the message box when the panel comes on screen, so opening chat
  // leaves a keyboard user able to type. The panel host focuses the panel
  // frame on open only when nothing inside has taken focus already, and
  // React runs this effect before the host's, so this one wins. Gated on
  // takesFocus so a session restore, which shows the panel with no gesture
  // behind it, does not drag focus here on page load.
  useEffect(() => {
    const becameVisible = isVisible && !wasVisible.current;
    wasVisible.current = isVisible;
    if (becameVisible && takesFocus) inputRef.current?.focus();
  }, [isVisible, takesFocus]);

  // Return focus to the message box once a decision is recorded. The
  // approval card's buttons unmount at that point (it switches to showing
  // the outcome), and with nothing left to hand focus to, a keyboard or
  // screen-reader user would otherwise be dropped to <body>. Compares
  // against the previous count rather than testing it against zero, so this
  // never fires on mount, including a hypothetical mount where the count
  // already has decisions behind it.
  const lastApprovalDecisionCount = useRef(approvalDecisionCount);
  useEffect(() => {
    if (approvalDecisionCount !== lastApprovalDecisionCount.current) {
      lastApprovalDecisionCount.current = approvalDecisionCount;
      inputRef.current?.focus();
    }
  }, [approvalDecisionCount]);

  // Move focus to the card when a turn pauses on an approval, so the
  // question is not missed. Driven by the arrival count for the same reason
  // the return above is driven by the decision count: the card itself cannot
  // tell a question just asked from one a reopened conversation brings back.
  // Only one approval is ever outstanding -- the backend refuses a turn
  // while one is -- so one handle covers them all.
  const approvalRef = useRef<HTMLDivElement>(null);
  const lastApprovalArrivalCount = useRef(approvalArrivalCount);
  useEffect(() => {
    if (approvalArrivalCount !== lastApprovalArrivalCount.current) {
      lastApprovalArrivalCount.current = approvalArrivalCount;
      approvalRef.current?.focus();
    }
  }, [approvalArrivalCount]);

  // Move focus to the message box when a turn someone pressed Stop on ends:
  // Stop unmounts with the turn and would drop focus to <body>, and the next
  // message is what continues the turn. Focus that has gone elsewhere is
  // left there, since a stop can wait on a load's cancel.
  const stopPressed = useRef(false);
  // aria-disabled rather than disabled, so pressing Stop, or a stop that
  // fails, leaves focus on the button instead of dropping it to <body>.
  const stopUnavailable = isStopping || !canStop;
  const wasLoadingForFocus = useRef(false);
  useEffect(() => {
    if (isLoading && !wasLoadingForFocus.current) stopPressed.current = false;
    if (isStopping) stopPressed.current = true;
    const active = document.activeElement;
    if (!isLoading && wasLoadingForFocus.current && stopPressed.current &&
        (active === null || active === document.body))
      inputRef.current?.focus();
    wasLoadingForFocus.current = isLoading;
  }, [isStopping, isLoading]);

  // What the live region carries. A turn is otherwise silent to a screen
  // reader from end to end: the progress surface and the reply that replaces
  // it are both just new content, which nothing announces. Numbered, because
  // a live region speaks on a DOM change and two steps can share a summary:
  // the same text set twice would be heard once.
  const [turnStatus, setTurnStatus] = useState({ seq: 0, text: '' });
  const wasLoading = useRef(false);
  const wasStopping = useRef(false);
  const announcedFailures = useRef(stopFailureCount);
  const announcedStep = useRef<number | null>(null);
  useEffect(() => {
    // Collected rather than set as each is found: two setTurnStatus calls in
    // one run land in the same React commit, and only the last text would
    // ever reach the region.
    const toAnnounce: string[] = [];
    if (isLoading) {
      if (!wasLoading.current) {
        announcedStep.current = null;
        toAnnounce.push('Working on it.');
      }
      if (isStopping && !wasStopping.current) toAnnounce.push('Stopping.');
      if (stopFailureCount !== announcedFailures.current)
        toAnnounce.push('Could not stop the turn.');
      const step = latestToolStep(trace);
      if (step !== null && step.step !== announcedStep.current) {
        announcedStep.current = step.step;
        toAnnounce.push(step.summary);
      }
    } else if (wasLoading.current) {
      toAnnounce.push(turnOutcome(chatMessages));
    }
    if (toAnnounce.length > 0) {
      const text = toAnnounce.join(' ');
      setTurnStatus((prev) => ({ seq: prev.seq + 1, text }));
    }
    wasLoading.current = isLoading;
    wasStopping.current = isStopping;
    announcedFailures.current = stopFailureCount;
  }, [isLoading, isStopping, stopFailureCount, trace, chatMessages]);

  // Handle Search Highlighting.
  useEffect(() => {
    if (!chatHistoryRef.current) return;

    let isCancelled = false;
    const instance = new Mark(chatHistoryRef.current);

    // Capture current active match position before unmarking.
    let prevActiveRect: DOMRect | null = null;
    if (currentMatch > 0) {
      const marks = chatHistoryRef.current.querySelectorAll<HTMLElement>(
          '.search-highlight');
      if (marks[currentMatch - 1]) {
        prevActiveRect = marks[currentMatch - 1].getBoundingClientRect();
      }
    }

    instance.unmark();

    if (searchTerm) {
      // Determine if this is a "refinement" of the previous search (e.g.
      // typing or backspacing).  We do this case-insensitively to match
      // standard search behavior.
      const lowerSearch = searchTerm.toLowerCase();
      const lowerPrev = prevSearchTermRef.current ?
                        prevSearchTermRef.current.toLowerCase() : '';
      const isRefining = lowerPrev && (lowerSearch.includes(lowerPrev) ||
                                       lowerPrev.includes(lowerSearch));

      // Update ref for next time.
      prevSearchTermRef.current = searchTerm;

      instance.mark(searchTerm, {
        element: 'span',
        className: 'search-highlight',
        separateWordSearch: false,
        // The Stop button lives inside this same node while a turn runs;
        // without this, searching its own label counts as a match and
        // mark.js can split its text node in two. The streaming reply is
        // excluded too: React rewrites its text nodes on every poll without
        // this effect re-running, and a highlight spliced into one is left
        // behind beside the new text. mark.js tests a text node's parent
        // alone, hence the descendant selector.
        exclude: ['.chat-stop', '.chat-partial-reply *'],
        done: (totalMatches) => {
          if (isCancelled) return;

          setMatchCount(totalMatches);
          if (totalMatches === 0) {
            setCurrentMatch(0);
            scrollToMatchRef.current = null;
          } else if (isRefining && prevActiveRect) {
            // Find closest match to previous position.
            const newMarks =
              chatHistoryRef.current?.querySelectorAll<HTMLElement>(
                '.search-highlight');
            if (!newMarks) return;
            let closestIdx = 0;
            let minDiff = Number.MAX_VALUE;

            if (!prevActiveRect)
              return;

            newMarks.forEach((mark, idx) => {
              // Prioritize same row by applying a heavy penalty to vertical
              // distance.  This ensures matches on the same line are always
              // chosen first, regardless of how far away they are horizontally.
              const rect = mark.getBoundingClientRect();
              const vDiff = Math.abs(rect.top - prevActiveRect.top);
              const hDiff = Math.abs(rect.left - prevActiveRect.left);
              const diff = vDiff * 10000 + hDiff;

              if (diff < minDiff) {
                minDiff = diff;
                closestIdx = idx;
              }
            });
            const next = closestIdx + 1;
            scrollToMatchRef.current = next;
            setCurrentMatch(next);
          } else if (isRefining) {
            // On content change, maintain position if possible, otherwise
            // clamp.
            setCurrentMatch(prev => {
              const next = Math.min(Math.max(1, prev), totalMatches);
              scrollToMatchRef.current = next;
              return next;
            });
          } else {
            // New distinct search - start at top.
            scrollToMatchRef.current = 1;
            setCurrentMatch(1);
          }
        },
        each: (element) => {
          // Base style for all matches.
          (element as HTMLElement).style.backgroundColor = MATCH_COLOR;
        }
      });
    } else {
      setMatchCount(0);
      setCurrentMatch(0);
      prevSearchTermRef.current = '';
      scrollToMatchRef.current = null;
    }

    return () => {
      isCancelled = true;
    };
  // currentMatch intentionally excluded — only need its value at time of
  // search change, not on every match navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, chatMessages, MATCH_COLOR]);

  // Handle Active Match Navigation (Styling & Scrolling).
  useEffect(() => {
    if (!chatHistoryRef.current || matchCount === 0 || currentMatch === 0)
      return;

    const marks =
      chatHistoryRef.current.querySelectorAll<HTMLElement>('.search-highlight');

    // Reset all to match color.
    marks.forEach(mark => {
      mark.style.backgroundColor = MATCH_COLOR;
    });

    // Highlight active match in orange.  currentMatch is 1-based index.
    const activeMark = marks[currentMatch - 1];
    if (activeMark) {
      activeMark.style.backgroundColor = ACTIVE_COLOR;
      if (scrollToMatchRef.current === currentMatch) {
        activeMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrollToMatchRef.current = null;
      }
    }

  }, [currentMatch, matchCount, searchTerm, ACTIVE_COLOR, MATCH_COLOR]);

  const handleNextMatch = () => {
    if (matchCount === 0) return;
    const nextMatch = currentMatch >= matchCount ? 1 : currentMatch + 1;
    scrollToMatchRef.current = nextMatch;
    setCurrentMatch(nextMatch);
  };

  const handlePrevMatch = () => {
    if (matchCount === 0) return;
    const prevMatch = currentMatch <= 1 ? matchCount : currentMatch - 1;
    scrollToMatchRef.current = prevMatch;
    setCurrentMatch(prevMatch);
  };

  // Which reason, if any, blocks sending. Both are true for as long as a
  // decision is in flight -- the turn it resumes starts before the decision
  // reaches the transcript -- and the running turn is the truer thing to say
  // then: the approval has been answered and is no longer waiting on anyone.
  const sendBlockReason: SendBlockReason | null =
    isLoading ? 'turn' :
    hasPendingApproval ? 'approval' :
    datasetsLoading ? 'datasets' : null;
  const sendBlock = sendBlockReason ? SEND_BLOCK_TEXT[sendBlockReason] : null;
  // Sending with no model would post an empty model id, and sending while
  // a turn is running or an approval is pending would draw the backend's
  // 409 refusal -- so the button and Enter key both stay silent instead of
  // surfacing that as a raw transcript error.
  const canSend = !noModel && sendBlock === null;

  const onSubmit = () => {
    if (question && canSend) {
      onSendMessage(question);
      setQuestion('');
    }
  };

  const handleEnter = useEnterSubmitHandler({
    onSubmit,
    value: question,
    setValue: setQuestion,
  });

  // Starting points for a conversation that has not started, as rows of
  // chips above the message box: the header's dataset first, then one row
  // for each of up to OTHER_DATASET_ROWS other datasets. A chip fills the
  // box and nothing else: the header stays the one control over what the
  // assistant works on, and a message naming another dataset brings that
  // schema into the model's context beside the active one's. Chips and not
  // ghost text in the box: a chat's box names its purpose and nothing in it
  // is accepted by a key, which is what every chat product a user has met
  // does, so a suggestion shown there was read as decoration. Gone once
  // there is a transcript, when they would be stale, and withheld with no
  // model, when they would offer a prompt that cannot be sent.
  const notes = datasetNotes(chatMessages);
  const suggestionRows: string[][] =
    chatMessages.length === 0 && !noModel && activeDataset !== null ?
      [activeDataset, ...otherDatasets.slice(0, OTHER_DATASET_ROWS)]
        .map(promptsFor) :
      [];
  // What describes the message box: the no-model notice when there is one
  // to give (the more fundamental problem, so it wins over a send block),
  // else why sending is blocked.
  const inputDescribedBy = noModel ? NO_MODEL_HINT_ID :
                           sendBlock ? sendBlock.hintId :
                           undefined;

  const takeSuggestion = (suggestion: string) => {
    setQuestion(suggestion);
    inputRef.current?.focus();
  };

  return (
    <>
      <div
        data-joyride-id={CHAT_TOUR_ANCHORS.toolbar}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          backgroundColor: theme.palette.chatbot.background,
          borderBottom: theme.border.default,
        }}
      >
        <TextField
          placeholder="Search Chat History"
          size="small"
          value={searchTerm}
          sx={{
            marginLeft: '4px',
            marginRight: '12px',
            flexGrow: 1,
            // Ensure input height matches buttons.
            '& .MuiInputBase-root': { paddingRight: '8px' }
          }}
          variant="outlined"
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matchCount > 0) {
              e.preventDefault();
              handleNextMatch();
            }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.5 }}>
                  {searchTerm && (
                    <Typography variant="caption" sx={{ marginRight: 1, whiteSpace: 'nowrap' }}>
                      {currentMatch}/{matchCount}
                    </Typography>
                  )}
                  {/* Always render buttons to maintain height, but disable/hide
                      if needed or just conditionally render if layout shift
                      isn't a huge concern.  Here we render conditionally but
                      the InputAdornment usually keeps things steady.
                  */}
                  <Tooltip describeChild title="Clear Search">
                    <span>
                      <IconButton
                        aria-label="Clear search"
                        size="small"
                        onClick={() => setSearchTerm('')}
                        disabled={!searchTerm}
                        sx={{ padding: '2px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip describeChild title="Previous Match">
                    <span>
                      <IconButton
                        aria-label="Previous match"
                        size="small"
                        onClick={handlePrevMatch}
                        disabled={matchCount === 0}
                        sx={{ padding: '2px' }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip describeChild title="Next Match">
                    <span>
                      <IconButton
                        aria-label="Next match"
                        size="small"
                        onClick={handleNextMatch}
                        disabled={matchCount === 0}
                      sx={{ padding: '2px' }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
            }
          }}
        />

        <Tooltip describeChild title="Clear Chat">
          <span>
            <Button
              variant="outlined"
              disabled={chatMessages.length === 0}
              onClick={onClearChatHistory}
              startIcon={<ClearAllIcon />}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Clear
            </Button>
          </span>
        </Tooltip>
      </div>

      <div
        style={{
          flexGrow: 1,
          maxHeight: 'calc(100% - 100px)',
          overflowY: 'auto',
          padding: '10px',
          marginBottom: '10px',
          position: 'relative',
        }}
        ref={chatHistoryRef}
        onScroll={handleTranscriptScroll}
      >
        {chatMessages.length === 0 && (
          <Typography sx={{ color: 'text.secondary', padding: '1px 17px' }}>
            Hello! How can I help you today?
          </Typography>
        )}
        {suggestionRows.map((row, rowIndex) => (
          <React.Fragment key={row[0]}>
            {/* The header's row is what the turn carries; the divider says
                the rest are alternatives, not more of the same. */}
            {rowIndex === 1 && (
              <Divider sx={{ mx: '17px', my: 0.5, color: 'text.secondary',
                             fontSize: '0.8rem' }}>
                or
              </Divider>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1,
                       padding: '8px 17px' }}>
              {row.map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  variant="outlined"
                  size="small"
                  onClick={() => takeSuggestion(suggestion)}
                />
              ))}
            </Box>
          </React.Fragment>
        ))}
        {chatMessages.map((msg, index) => {
          const approval = msg.approval;
          const note = notes.get(index);
          return (
            <React.Fragment key={index}>
              {note !== undefined && (
                <DatasetNote role="note">{datasetNoteText(note)}</DatasetNote>
              )}
              {msg.stopped && msg.partial && (
                <PartialReply content={msg.partial} theme={theme} />
              )}
              <ChatMessageItem msg={msg} theme={theme} />
              {msg.stopped && msg.trace !== undefined &&
                msg.trace.length > 0 && <TraceList entries={msg.trace} />}
              {approval && (
                <ApprovalCard
                  approval={approval}
                  ref={approval.decision === null ? approvalRef : undefined}
                  isBusy={isDeciding}
                  onDecide={(decision) => onDecide(approval.id, decision)}
                />
              )}
              {msg.unapplied?.map((action, actionIndex) => (
                <UnappliedAction key={actionIndex} action={action}
                  onNavigate={onNavigate} />
              ))}
            </React.Fragment>
          );
        })}
        {isLoading && (
          <>
            {partialText && (
              <div className="chat-partial-reply"
                data-testid="chat-partial-reply" aria-busy="true">
                <PartialReply content={partialText} theme={theme} />
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                marginTop: '10px',
                fontSize: '14px',
              }}
            >
              <CircularProgress size={24} aria-hidden
                sx={{ ml: '17px', mr: '8px' }} />
              <span>Thinking...</span>
              {/* Separate from Send, which stays blocked until the backend
                  confirms the stop: the turn lock is held until then. The
                  name carries the visible label, so a voice-control user
                  can say what they see. Escape from the panel body does
                  the same (ChatPanelBody), which the shortcut attribute
                  and the tooltip say; the tooltip describes rather than
                  names, so the name stays the label. */}
              <Tooltip title="Esc" describeChild>
                <Button
                  aria-label={
                    isStopping ? 'Stopping the turn' : 'Stop the turn'
                  }
                  aria-disabled={stopUnavailable}
                  aria-keyshortcuts="Escape"
                  className="chat-stop"
                  size="small"
                  variant="outlined"
                  startIcon={<StopIcon />}
                  disableRipple={stopUnavailable}
                  onClick={stopUnavailable ? undefined : onStop}
                  sx={{
                    ml: 2,
                    '&[aria-disabled="true"]': {
                      color: 'action.disabled',
                      borderColor: 'action.disabledBackground',
                      cursor: 'default'
                    }
                  }}
                >
                  {isStopping ? 'Stopping...' : 'Stop'}
                </Button>
              </Tooltip>
            </div>
          </>
        )}
        {isLoading && <TraceList entries={trace} />}
      </div>

      {/* Outside the transcript: the search highlighter runs over that node,
          and text only a screen reader reads must not be searched or
          counted as a match. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        <span key={turnStatus.seq}>{turnStatus.text}</span>
      </Box>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px',
        }}
      >
        <TextField
          data-joyride-id={CHAT_TOUR_ANCHORS.message}
          inputRef={inputRef}
          sx={{ flexGrow: 1, marginRight: '8px' }}
          placeholder={noModel ?
                       NO_MODEL_SHORT :
                       sendBlock?.short ?? `Message ${activeLlmName}`}
          size="small"
          variant="outlined"
          value={question}
          multiline
          helperText={noModel ? NO_MODEL_TEXT : sendBlock?.text}
          slotProps={{
            formHelperText: { id: inputDescribedBy },
            htmlInput: {
              onKeyDown: handleEnter,
              // Named for what it is: with no label the placeholder would be
              // the name, and a blocked state's placeholder says why it is
              // blocked, not what the box is for.
              'aria-label': noModel ?
                            'Message (no AI model available)' :
                            sendBlock?.ariaLabel ??
                            `Message ${activeLlmName}`,
              'aria-describedby': inputDescribedBy
            }
          }}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <Tooltip describeChild title="Submit">
          <span>
            <Button
              aria-label="Submit"
              variant="contained"
              disabled={!question || !canSend}
              onClick={onSubmit}
              color="primary"
              sx={{ borderRadius: '4px', paddingX: '6px', minWidth: 'auto',
                   '& .MuiButton-startIcon': {
                     marginRight: '0px',
                     marginLeft: '0px'
                   }
                 }}
              startIcon={<ArrowCircleUpIcon />}
            />
          </span>
        </Tooltip>
      </div>
    </>
  );
};

export default ChatContent;
